import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { Checkbox } from "@/components/ui/checkbox";
import { bytesToBase64, textToBase64, base64ToBytes, utf8Decode, base64Problem, bytesToHex } from "@/lib/utf8";
import { downloadBlob } from "@/components/ToolLayout";
import { fileSizeProblem, fileSizeLabel, toDataUri, dataUriMime, extensionForMime, MAX_FILE_BYTES } from "./base64File";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Encodes text or data into Base64 format, or decodes Base64 back into its original form — a common encoding used to safely represent binary data as plain text, often seen in data URLs, email attachments, and API tokens.`,
  how: `Paste your text or Base64 string and press Encode or Decode. To encode a file, choose it under "Encode a file" (up to ${fileSizeLabel(MAX_FILE_BYTES)}; it never leaves your browser) and copy or download the data URI or Base64. Decoding binary data or a data: URL gives you a Download file button. Text is converted as UTF-8, so any language and emoji work, and decoding accepts standard or URL-safe Base64, missing padding, and data: URLs.`,
  tip: `Base64 encoding is not encryption — it's a reversible format for safely representing data as text, so don't rely on it to keep anything actually secret or secure.`,
};

export default function Base64Tool() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [urlSafe, setUrlSafe] = useState(false);
  const [error, setError] = useState("");
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  const [decodedMime, setDecodedMime] = useState<string | null>(null);
  const [file, setFile] = useState<{ name: string; size: number; b64: string; mime: string } | null>(null);
  const [fileError, setFileError] = useState("");
  const [asDataUri, setAsDataUri] = useState(true);

  const { logUsageNow } = useLogUsageOnAction();

  // Confirmed as a real, reported gap, not assumed: this was
  // useLogToolUsage(output, undefined, { ownActions: true }), a debounced log that fires after the
  // click already set and displayed output, same root cause already
  // found and fixed in Case Converter/Text Reverser/UUID Generator --
  // a QA retest found this tool (and several siblings) were never
  // migrated. logUsageNow() is the same gating pattern Merge PDF uses
  // for its own discrete action: await it first, don't run the
  // transform if it returns false.
  const encode = async () => {
    const allowed = await logUsageNow("convert");
    if (!allowed) return;
    setError(""); setBytes(null);
    setOutput(textToBase64(input, urlSafe));
  };
  const decode = async () => {
    // Check first: invalid input is never charged and never leaves an older result on screen.
    const problem = base64Problem(input);
    if (problem) { setOutput(""); setBytes(null); setError(problem.message); return; }
    let raw: Uint8Array;
    try { raw = base64ToBytes(input); }
    catch { setOutput(""); setBytes(null); setError("Not valid Base64."); return; }
    const allowed = await logUsageNow("convert");
    if (!allowed) return;
    setError(""); setDecodedMime(dataUriMime(input));
    try {
      setOutput(utf8Decode(raw));
      setBytes(dataUriMime(input) ? raw : null);
      setDecodedMime(dataUriMime(input));
    } catch {
      setOutput(bytesToHex(raw));
      setBytes(raw);
      setError("The decoded bytes are not UTF-8 text (likely binary data), so they are shown as hex. You can also download them as a .bin file.");
    }
  };

  const pickFile = async (f: File | undefined) => {
    setFile(null); setFileError("");
    if (!f) return;
    const problem = fileSizeProblem(f.size);
    if (problem) { setFileError(problem); return; }
    try {
      const raw = new Uint8Array(await f.arrayBuffer());
      setFile({ name: f.name, size: f.size, b64: bytesToBase64(raw, urlSafe), mime: f.type });
    } catch { setFileError("This file could not be read. Try choosing it again."); }
  };
  const fileOut = file ? (asDataUri ? toDataUri(file.b64, file.mime) : file.b64) : "";
  const copyFileOut = async () => {
    if (!(await logUsageNow("copy"))) return;
    try { await navigator.clipboard.writeText(fileOut); toast.success("Copied!"); } catch { toast.error("Copy was blocked by the browser. Use Download .txt instead."); }
  };
  const downloadFileOut = () => downloadBlob(new Blob([fileOut], { type: "text/plain" }), `${file!.name.replace(/\.[^.]*$/, "") || "file"}.base64.txt`);

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label htmlFor="b64-input">Input</Label><Textarea id="b64-input" value={input} onChange={e => { setInput(e.target.value); setError(""); }} aria-invalid={!!error && !bytes} placeholder="Enter text to encode/decode" className="min-h-[120px] font-mono" /></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={urlSafe} onCheckedChange={v => setUrlSafe(v === true)} />
          URL-safe Base64 when encoding (- and _, no padding). Decoding accepts both.
        </label>
        <div className="flex gap-2">
          <Button onClick={encode}>Encode</Button>
          <Button variant="outline" onClick={decode}>Decode</Button>
        </div>
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="b64-file">Encode a file (stays in your browser, up to {fileSizeLabel(MAX_FILE_BYTES)})</Label>
          <input id="b64-file" type="file" onChange={e => { void pickFile(e.target.files?.[0]); e.target.value = ""; }} className="block text-sm" aria-invalid={!!fileError} />
          {fileError && <p role="alert" className="text-xs text-destructive" data-testid="b64-file-error">{fileError}</p>}
          {file && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{file.name} · {fileSizeLabel(file.size)}</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={asDataUri} onCheckedChange={v => setAsDataUri(v === true)} />Include data: URI prefix</label>
              <Textarea aria-label="File Base64 output" value={fileOut} readOnly className="min-h-[100px] font-mono text-xs" />
              <div className="flex gap-2"><Button size="sm" onClick={copyFileOut}>Copy</Button><Button size="sm" variant="outline" onClick={downloadFileOut}>Download .txt</Button></div>
            </div>
          )}
        </div>
        {error && <p role="alert" className="text-xs text-destructive" data-testid="b64-error">{error}</p>}
        {output && (
          <div><Label htmlFor="b64-output">Output</Label><Textarea id="b64-output" value={output} readOnly className="min-h-[120px] font-mono" />
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => { void copyText(output, "Copied!"); }}>Copy</Button>
              {bytes && <Button size="sm" variant="outline" onClick={() => downloadBlob(new Blob([bytes as BlobPart], { type: decodedMime || "application/octet-stream" }), `decoded.${extensionForMime(decodedMime)}`)}>Download file</Button>}
            </div>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
